// NotificationConfigModal.tsx
import { Modal, Form, Input, Select, Switch, Space, Button, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { useSuperAdmin } from '../../../contexts/SuperAdminContext';
import { useQueryClient } from '@tanstack/react-query';

interface NotificationConfigFormValues {
  panelEndpointId: string;
  notificationType: string;
  useFlow: boolean;
  flowId?: string;
  messageTemplate: string;
}

interface NotificationConfigModalProps {
  open: boolean;
  onClose: () => void;
  endpointId: string;
  initialData?: {
    id: string;
    notificationType: string;
    useFlow: boolean;
    flowId?: string;
    messageTemplate: string;
  };
  mode: 'create' | 'edit';
}

export const NotificationConfigModal = ({ 
  open, 
  onClose, 
  endpointId,
  initialData,
  mode 
}: NotificationConfigModalProps) => {
  const [form] = Form.useForm<NotificationConfigFormValues>();
  const { notificationTypes: contextNotificationTypes = [], createNotificationConfig, updateNotificationConfig } = useSuperAdmin();
  const queryClient = useQueryClient();
  
  // Use context types if available, otherwise use default
const notificationTypes = (() => {
  // First try to use context notification types if available and valid
  if (contextNotificationTypes && Array.isArray(contextNotificationTypes) && 
      contextNotificationTypes.length > 0 && 
      contextNotificationTypes.every(type => type && type.value && type.label)) {
    return contextNotificationTypes;
  }
  
  // Otherwise use our complete default list from the Prisma schema
  return [
    { value: 'DEPOSIT_COMPLETED', label: 'Depósito Completado' },
    { value: 'DEPOSIT_GAMIFIED', label: 'Depósito Gamificado' },
    { value: 'DEPOSIT_VALIDATION', label: 'Validación de Depósito' },
    { value: 'DEPOSIT_REJECTION', label: 'Rechazo de Depósito' },
    { value: 'WITHDRAW_APPROVAL', label: 'Aprobación de Retiro' },
    { value: 'WITHDRAW_REJECTION', label: 'Rechazo de Retiro' },
    { value: 'WITHDRAW_COMPLETED', label: 'Retiro Completado' },
    { value: 'USER_CREATED', label: 'Usuario Creado' },
    { value: 'USER_REJECTED', label: 'Usuario Rechazado' }
  ] as const;
})();

    
  const [useFlow, setUseFlow] = useState(initialData?.useFlow || false);
  
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      form.setFieldsValue({
        ...initialData,
        panelEndpointId: endpointId
      });
      setUseFlow(initialData.useFlow);
    } else {
      form.setFieldsValue({
        panelEndpointId: endpointId,
        useFlow: false,
        messageTemplate: ''
      });
      setUseFlow(false);
    }
  }, [form, initialData, mode, endpointId]);

  const handleSubmit = async (values: NotificationConfigFormValues) => {
    try {
      const data = {
        ...values,
        panelEndpointId: endpointId
      };

      if (mode === 'edit' && initialData) {
        await updateNotificationConfig({
          configId: initialData.id,
          data
        });
        message.success('Configuración de notificación actualizada correctamente');
      } else {
        await createNotificationConfig(data);
        message.success('Configuración de notificación creada correctamente');
        
        // Delay adicional para asegurar que el backend procese completamente
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Invalidar queries para asegurar actualización inmediata
      queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
      
      // Forzar una segunda invalidación después de un delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      }, 100);
      
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error saving notification config:', error);
      message.error('Error al guardar la configuración de notificación');
    }
  };

  const handleUseFlowChange = (checked: boolean) => {
    setUseFlow(checked);
  };

  const getTemplateExample = (type: string) => {
    switch (type) {
      case 'DEPOSIT_COMPLETED':
        return "Hola {username}, tu carga de ${amount} fue procesada. Operación #{operation_id}";
      case 'DEPOSIT_GAMIFIED':
        return "🎮 ¡Felicidades {username}! Tu carga de ${amount} fue procesada. Bonus: ${bonus}. {gamification_message}";
      case 'DEPOSIT_VALIDATION':
        return "Hola {username}, tu carga está siendo validada. Te notificaremos al completarse.";
      case 'DEPOSIT_REJECTION':
        return "Hola {username}, tu carga fue rechazada. Motivo: {rejection_reason}";
      case 'WITHDRAW_APPROVAL':
        return "Hola {username}, tu solicitud de retiro de ${monto_retiro} fue aprobada.";
      case 'WITHDRAW_REJECTION':
        return "Hola {username}, tu retiro fue rechazado. Motivo: {rejection_reason}";
      case 'WITHDRAW_COMPLETED':
        return "Hola {username}, tu retiro de ${monto_retiro} fue procesado correctamente.";
      case 'USER_CREATED':
        return "Hola {username}, tu cuenta fue creada exitosamente.";
      case 'USER_REJECTED':
        return "Hola {username}, tu solicitud de cuenta fue rechazada.";
      default:
        return "Hola {username}, este es un mensaje de notificación.";
    }
  };

  return (
    <Modal
      title={`${mode === 'edit' ? 'Editar' : 'Crear'} Configuración de Notificación`}
      open={open}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="panelEndpointId"
          hidden
        >
          <Input />
        </Form.Item>
        
        <Form.Item
          label="Tipo de Notificación"
          name="notificationType"
          rules={[{ required: true, message: 'Seleccione un tipo de notificación' }]}
        >
          <Select
            placeholder="Seleccione un tipo de notificación"
            onChange={(value) => {
              // Actualizar el template por defecto cuando cambia el tipo
              form.setFieldsValue({
                messageTemplate: getTemplateExample(value)
              });
            }}
          >
{notificationTypes.map((type, index) => (
  <Select.Option 
    key={type.value || `notification-type-${index}`} 
    value={type.value}
  >
    {type.label}
  </Select.Option>
))}
          </Select>
        </Form.Item>
        
        <Form.Item
          label="Usar Flujo"
          name="useFlow"
          valuePropName="checked"
        >
          <Switch onChange={handleUseFlowChange} />
        </Form.Item>
        
        {useFlow && (
          <Form.Item
            label="ID del Flujo"
            name="flowId"
            rules={[{ required: useFlow, message: 'Ingrese el ID del flujo' }]}
          >
            <Input placeholder="ID del flujo en Whapify/Manychat" />
          </Form.Item>
        )}
        
        <Form.Item
          label="Plantilla de Mensaje"
          name="messageTemplate"
          rules={[{ required: !useFlow, message: 'Ingrese una plantilla de mensaje' }]}
          tooltip={useFlow ? "Este mensaje no se enviará si se usa un flujo, pero puede servir como referencia" : "Use {variable} para insertar variables dinámicas"}
        >
          <Input.TextArea 
            rows={4} 
            placeholder="Ej: Hola {username}, tu operación fue exitosa"
          />
        </Form.Item>
        
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          <strong>Variables disponibles:</strong> {"{username}"}, {"{amount}"}, {"{operation_id}"}, {"{rejection_reason}"}, {"{monto_retiro}"}, {"{bonus}"}, {"{gamification_message}"}, {"{level}"}, {"{streak}"}, etc.
        </Typography.Paragraph>
        
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit">
              {mode === 'create' ? 'Crear' : 'Actualizar'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};